import { useEffect, useRef } from "react";
import { useNavigate } from "react-router";
import { FaUserDoctor } from "react-icons/fa6";
import { useLoader } from "@/app/hooks/useLoader";
import { useRouter } from "@/app/hooks/useRouter";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import DataTable from "@/app/components/ui/Reusable/DataTable";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import SolidButton from "@/app/components/ui/Buttons/SolidButton";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { IConfigurationListState } from "@/app_redux/reducers/slices/auth/configuration_slice";
import { useConfigurationModuleSearchAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationModuleSearchAPI";
import { useConfigurationStaffCategoryListAPI } from "@/app/hooks/api_hooks/configuration/useConfigurationStaffCategoryListAPI";
import { getStaffCategoryColumns } from "@/app/hooks/custom_hooks";
import StructuredTableSkeleton from "@/app/components/ui/Skeletons/StructuredTableSkeleton";

const ConfigurationStaffCategorySetup = () => {
  const hasFetchedRef = useRef(false);
  const { NO_APPOINTMENTS } = useImageConstants();
  const isStaffCategoryListInProgress = useLoader("isOnboardStaffCategoryListInProgress");
  const { getRouteKey } = useRouter();
  const navigate = useNavigate();
  const fetchStaffCategoryList = useConfigurationStaffCategoryListAPI();
  const routeIdConfig = getRouteKey("CONFIGURATION", "id");
  const { routeState } = useRouteInfo(routeIdConfig);
  const { staffCategoryList: staffCategoryListData } = routeState.state as IConfigurationListState;
  const searchStaffCategory = useConfigurationModuleSearchAPI("staff_category");
  const columns = getStaffCategoryColumns(navigate, getRouteKey);

  useEffect(() => {
    if (!hasFetchedRef.current) {
      fetchStaffCategoryList();
      hasFetchedRef.current = true;
    }
  }, []);

  return (
    <div className="px-5 py-3">
      <div className="flex flex-row items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-semibold text-sm sm:text-base text-gray-900 leading-6 whitespace-nowrap">
            Staff Category List
          </h1>
          <p className="mt-2 text-gray-700 text-sm hidden sm:block">
            A list of all the staff categories that will be mapped to the staff when they are onboarded.
          </p>
        </div>
        <div className="shrink-0">
          <SolidButton asLink href={getRouteKey("STAFF_CATEGORY_SETUP_ONBOARD", "url")} size="XL">
            Add Staff Category
          </SolidButton>
        </div>
      </div>
      <div className="mt-3 w-full sm:w-1/2">
        <InputWithAddOn
          onEnterKeyPress={(query: string) => {
            if (query.length === 0) {
              fetchStaffCategoryList();
            } else {
              searchStaffCategory(query);
            }
          }}
          containerClassName="w-full sm:w-1/2"
          addon={<FaUserDoctor className="w-4 h-4" />}
          placeholder="Search Staff Category"
        />
      </div>
      {isStaffCategoryListInProgress ? (
        <div className="mt-6">
          <StructuredTableSkeleton columns={columns.length} />
        </div>
      ) : staffCategoryListData?.length > 0 ? (
        <div>
          <DataTable data={staffCategoryListData || []} columns={columns} maxHeight="600px" />
        </div>
      ) : (
        <NoDataFoundComponent
          imageURL={NO_APPOINTMENTS}
          message="No Staff Categories"
          description="No staff categories have been found, please onboard some staff categories."
        />
      )}
    </div>
  );
};

export default ConfigurationStaffCategorySetup;
